/**
 * Pre-Meeting Preparation View Types
 *
 * Story 3.8: Pre-Meeting Preparation View
 *
 * Types for meeting prep, attendee research, talking points,
 * and context injection into the coaching service.
 */
import { z } from 'zod';
import { AttendeeResponseStatus } from './calendar.js';
// =============================================================================
// Research Status Types
// =============================================================================
export const AttendeeResearchStatusType = {
    LOADING: 'loading',
    CACHED: 'cached',
    STALE: 'stale',
    ERROR: 'error',
    NONE: 'none',
};
export const AttendeeResearchStatusSchema = z.object({
    status: z.enum(Object.values(AttendeeResearchStatusType)),
    researchedAt: z.string().datetime().optional(),
    error: z.string().optional(),
    isStale: z.boolean().optional(),
});
export const AttendeeWithResearchSchema = z.object({
    email: z.string().email(),
    name: z.string().nullable(),
    responseStatus: z.enum(Object.values(AttendeeResponseStatus)),
    isOrganizer: z.boolean(),
    isAppUser: z.boolean().optional(),
    appUserRole: z.string().nullable().optional(),
    research: z
        .object({
        email: z.string().email(),
        name: z.string().nullable(),
        currentTitle: z.string().nullable(),
        currentCompany: z.string().nullable(),
        linkedinUrl: z.string().nullable(),
        linkedinSummary: z.string().nullable(),
        shortSummary: z.string().nullable().optional(),
        companyBackground: z.string().nullable(),
        recentNews: z.array(z.string()),
        interests: z.array(z.string()),
        potentialTalkingPoints: z.array(z.string()),
        researchedAt: z.string(),
    })
        .nullable(),
    researchStatus: AttendeeResearchStatusSchema,
    previousMeetings: z.array(z.object({
        meetingId: z.string().uuid(),
        title: z.string(),
        date: z.string(),
        summaryExcerpt: z.string().nullable(),
    })),
});
export const PreviousMeetingSummarySchema = z.object({
    meetingId: z.string().uuid(),
    title: z.string(),
    date: z.string(),
    summaryExcerpt: z.string().nullable(),
});
export const TalkingPointsSchema = z.object({
    opening: z.array(z.string()),
    keyTopics: z.array(z.string()),
    potentialObjections: z.array(z.string()),
    closing: z.array(z.string()),
    generatedAt: z.string().datetime(),
});
export const PrepDataSchema = z.object({
    meeting: z.any(), // CalendarEventSchema is complex, using any for now
    attendees: z.array(AttendeeWithResearchSchema),
    talkingPoints: TalkingPointsSchema.nullable(),
    previousMeetings: z.array(PreviousMeetingSummarySchema),
    researchInProgress: z.array(z.string().email()),
    primaryObjective: z.string().nullable(),
    expectedOutcome: z.string().nullable(),
    prepGeneratedAt: z.string().nullable(),
    prepFrozen: z.boolean(),
    meetingIdentity: z.string().nullable(),
    generatedByUserId: z.string().nullable(),
});
export const PrepContextSchema = z.object({
    meeting: z.object({
        id: z.string(),
        title: z.string(),
        clientId: z.string().nullable(),
        clientName: z.string().nullable(),
        dealId: z.string().nullable(),
        dealName: z.string().nullable(),
        dealStage: z.string().nullable(),
        dealValue: z.number().nullable(),
    }),
    attendees: z.array(z.object({
        name: z.string().nullable(),
        email: z.string().email(),
        title: z.string().nullable(),
        role: z.string().nullable(),
        interests: z.array(z.string()),
        previousMeetings: z.array(PreviousMeetingSummarySchema),
    })),
    talkingPoints: TalkingPointsSchema.nullable(),
    knownObjections: z.array(z.string()),
    dealStage: z.string().nullable(),
    preparedAt: z.string().datetime(),
});
export const TriggerResearchResponseSchema = z.object({
    status: z.enum(['started', 'completed', 'already_cached', 'already_in_progress']),
    estimatedTimeSeconds: z.number().optional(),
    research: z.any().optional(), // AttendeeResearch schema
});
export const ResearchStatusResponseSchema = z.object({
    status: z.enum(['completed', 'in_progress', 'failed', 'not_found']),
    research: z.any().optional(),
    error: z.string().optional(),
});
export const RegenerateTalkingPointsRequestSchema = z.object({
    additionalInstructions: z.string().max(500).optional(),
});
export const RefreshAllResearchResponseSchema = z.object({
    started: z.array(z.string().email()),
    alreadyCached: z.array(z.string().email()),
    alreadyInProgress: z.array(z.string().email()),
});
export const PreviousEmailSummarySchema = z.object({
    emailId: z.string().uuid(),
    subject: z.string(),
    date: z.string().datetime(),
    snippet: z.string().nullable(),
    direction: z.enum(['sent', 'received']),
});
export const DealTaskSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    dueDate: z.string().datetime().nullable(),
    status: z.enum(['pending', 'completed']),
    assignee: z.string().nullable(),
});
